import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationItem } from '../components/notification/NotificationItem';
import { Button } from '../components/common/Button';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useLanguage } from '../i18n/useLanguage';
import './NotificationsPage.css';

export const NotificationsPage: React.FC = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    clearAll,
    refresh,
  } = useNotifications();

  // Redirect if not authenticated and fetch notifications on mount
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Fetch notifications when page mounts
  React.useEffect(() => {
    if (isAuthenticated && !isLoading) {
      refresh();
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm(t('notifications.confirmClearAll'))) {
      try {
        await clearAll();
      } catch (err) {
        console.error('Error clearing all notifications:', err);
      }
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const hasUnread = notifications.some(n => !n.read);

  return (
    <div className="notifications-page">
      <div className="notifications-page-header">
        <h1>{t('header.notifications')}</h1>
        <div className="notifications-page-header-info">
          {unreadCount > 0 && (
            <span className="notifications-unread-badge">
              {t('notifications.unreadBadge').replace('{count}', String(unreadCount))}
            </span>
          )}
          <Button onClick={refresh} disabled={isLoading}>
            {t('notifications.refresh')}
          </Button>
        </div>
      </div>

      <div className="notifications-page-actions">
        {hasUnread && (
          <Button onClick={handleMarkAllAsRead} disabled={isLoading}>
            {t('notifications.markAllRead')}
          </Button>
        )}
        {notifications.length > 0 && (
          <Button onClick={handleClearAll} disabled={isLoading} variant="danger">
            {t('notifications.clearAll')}
          </Button>
        )}
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="notifications-page-content">
        {isLoading && notifications.length === 0 ? (
          <div className="notifications-loading">
            <LoadingSpinner size="medium" />
            <span>{t('notifications.loading')}</span>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <svg 
              className="notifications-empty-icon" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p>{t('notifications.empty')}</p>
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onClose={() => {}}
                />
              ))}
            </div>
            {isLoading && (
              <div className="notifications-loading-more">
                <LoadingSpinner size="small" />
              </div>
            )}
            {hasMore && !isLoading && (
              <div className="notifications-load-more">
                <Button onClick={loadMore}>{t('notifications.loadMore')}</Button>
              </div>
            )}
            {!hasMore && notifications.length > 0 && (
              <div className="notifications-end">
                {t('notifications.noMore')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
